/** State chooser — every live state from the catalog, sorted A-Z, with
 * streamed size + build date. Current state highlighted. */
import { useEffect, useRef } from "react";
import type { StateEntry } from "../catalog";

/** Binary GiB, labeled "GB" (matches the size the download actually costs
 * on a phone's storage meter). */
function formatBytes(bytes: number): string {
  const gb = bytes / 1024 ** 3;
  if (gb >= 1) return `${gb.toFixed(1)} GB`;
  const mb = bytes / 1024 ** 2;
  return `${Math.max(1, Math.round(mb))} MB`;
}

export default function StatePicker({
  states,
  current,
  onPick,
  onClose,
}: {
  states: StateEntry[];
  current: StateEntry;
  onPick: (region: string) => void;
  onClose: () => void;
}) {
  const sorted = [...states].sort((a, b) => a.name.localeCompare(b.name));

  // Focus the first row on open — same reason as every other sheet in this
  // app (PlansSheet's newRouteRef, SearchPalette's inputRef): it's what
  // routes Escape below to this dialog's own handler instead of falling
  // through to whatever had focus before the sheet opened.
  const firstRowRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    firstRowRef.current?.focus();
  }, []);

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="states-heading"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            // stopPropagation matters here the same way it does for
            // PlanBar's overflow/closures menus: LAYERS (a sibling sheet)
            // stays reachable mid-draw, so a future change that lifts this
            // STATE-disabled-during-draw restriction shouldn't silently
            // reopen the "Escape also fires App's draw-mode handler" bug.
            e.stopPropagation();
            onClose();
          }
        }}
      >
        <h2 id="states-heading">States</h2>
        <ul className="picker-list">
          {sorted.map((s, i) => (
            <li key={s.region}>
              <button
                ref={i === 0 ? firstRowRef : undefined}
                className={s.region === current.region ? "row current" : "row"}
                onClick={() => onPick(s.region)}
                aria-current={s.region === current.region ? "true" : undefined}
              >
                <span>{s.name}</span>
                <span className="row-caption">
                  {formatBytes(s.total_bytes)} streamed{s.built ? ` · built ${s.built}` : ""}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
